// Reverse data mappers - Transform Excel row data to AWS SDK create operation format

import { normalizePhoneNumber } from './importValidationService.js';

/**
 * Parse hours of operation string from Excel format
 * Format: "Monday 08:00-17:00, Tuesday 08:00-17:00, ..."
 * Returns AWS SDK Config array format
 */
export const parseHoursOfOperationConfig = (hoursString) => {
  if (!hoursString || hoursString === 'N/A') {
    return [];
  }

  const config = [];
  
  // Map both full day names and abbreviations to AWS API format
  const dayMapping = {
    // Full names
    'Monday': 'MONDAY',
    'Tuesday': 'TUESDAY',
    'Wednesday': 'WEDNESDAY',
    'Thursday': 'THURSDAY',
    'Friday': 'FRIDAY',
    'Saturday': 'SATURDAY',
    'Sunday': 'SUNDAY',
    // Abbreviations (from export format)
    'M': 'MONDAY',
    'T': 'TUESDAY',
    'W': 'WEDNESDAY',
    'Th': 'THURSDAY',
    'F': 'FRIDAY',
    'Sa': 'SATURDAY',
    'Su': 'SUNDAY'
  };

  // Helper to parse 12-hour time format (e.g., "8:00 AM" or "5:00 PM") to hours/minutes
  const parse12HourTime = (timeStr) => {
    if (!timeStr) return null;
    
    const trimmed = timeStr.trim();
    
    // Check for 24/7 format
    if (trimmed === '24/7') {
      return { hours: 0, minutes: 0, is24x7: true };
    }
    
    // Try 12-hour format: "8:00 AM" or "12:30 PM"
    const match12Hour = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12Hour) {
      let hours = parseInt(match12Hour[1], 10);
      const minutes = parseInt(match12Hour[2], 10);
      const period = match12Hour[3].toUpperCase();
      
      // Convert to 24-hour format
      if (period === 'AM') {
        if (hours === 12) hours = 0; // 12:00 AM = 00:00
      } else { // PM
        if (hours !== 12) hours += 12; // 1:00 PM = 13:00, but 12:00 PM stays 12
      }
      
      return { hours, minutes };
    }
    
    // Try 24-hour format: "08:00" or "17:00"
    const match24Hour = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (match24Hour) {
      return {
        hours: parseInt(match24Hour[1], 10),
        minutes: parseInt(match24Hour[2], 10)
      };
    }
    
    return null;
  };

  // Split by comma to get each day entry
  const dayEntries = hoursString.split(',').map(s => s.trim());

  dayEntries.forEach(entry => {
    // Handle export format: "M @ 8:00 AM - 5:00 PM" or "M @ 24/7"
    if (entry.includes('@')) {
      const [dayPart, timePart] = entry.split('@').map(s => s.trim());
      const day = dayPart.trim();
      
      if (dayMapping[day]) {
        // Check for 24/7 format
        if (timePart.trim() === '24/7') {
          config.push({
            Day: dayMapping[day],
            StartTime: { Hours: 0, Minutes: 0 },
            EndTime: { Hours: 0, Minutes: 0 }
          });
        } else {
          // Parse time range: "8:00 AM - 5:00 PM"
          const timeParts = timePart.split('-').map(s => s.trim());
          if (timeParts.length === 2) {
            const startTime = parse12HourTime(timeParts[0]);
            const endTime = parse12HourTime(timeParts[1]);
            
            if (startTime && endTime) {
              config.push({
                Day: dayMapping[day],
                StartTime: { Hours: startTime.hours, Minutes: startTime.minutes },
                EndTime: { Hours: endTime.hours, Minutes: endTime.minutes }
              });
            }
          }
        }
      }
    } else {
      // Handle original format: "Monday 08:00-17:00"
      const parts = entry.split(' ');
      if (parts.length >= 2) {
        const day = parts[0].trim();
        const timeRange = parts[1].trim();

        if (dayMapping[day] && timeRange !== 'Closed') {
          const [startTime, endTime] = timeRange.split('-');

          if (startTime && endTime) {
            const startParsed = parse12HourTime(startTime);
            const endParsed = parse12HourTime(endTime);

            if (startParsed && endParsed) {
              config.push({
                Day: dayMapping[day],
                StartTime: { Hours: startParsed.hours, Minutes: startParsed.minutes },
                EndTime: { Hours: endParsed.hours, Minutes: endParsed.minutes }
              });
            }
          }
        }
      }
    }
  });

  return config;
};;

/**
 * Map Hours of Operation Excel row to AWS SDK CreateHoursOfOperationCommand input
 */
export const mapHoursOfOperationForCreate = (excelRow) => {
  return {
    Name: excelRow.name || '',
    Description: excelRow.description || '',
    TimeZone: excelRow.timeZone || '',
    Config: parseHoursOfOperationConfig(excelRow.hoursOfOperation),
    Tags: {} // Can be extended if tags are added to Excel
  };
};

/**
 * Map Agent Status Excel row to AWS SDK CreateAgentStatusCommand input
 */
export const mapAgentStatusForCreate = (excelRow) => {
  return {
    Name: excelRow.name || '',
    Description: excelRow.description || '',
    State: 'ENABLED', // Default to enabled
    DisplayOrder: excelRow.displayOrder || 1,
    Tags: {}
  };
};

/**
 * Map Queue Excel row to AWS SDK CreateQueueCommand input
 * Note: Requires resolved IDs for HoursOfOperation and OutboundFlow
 */
export const mapQueueForCreate = (excelRow, resolvedIds = {}) => {
  const input = {
    Name: excelRow.name || '',
    Description: excelRow.description || '',
    Tags: {}
  };

  // Resolve Hours of Operation ID from name
  if (excelRow.hoursOfOperationName && resolvedIds.hoursOfOperationNameToId) {
    input.HoursOfOperationId = resolvedIds.hoursOfOperationNameToId[excelRow.hoursOfOperationName];
  }

  // Add max contacts if present
  if (excelRow.maxContacts && excelRow.maxContacts > 0) {
    input.MaxContacts = parseInt(excelRow.maxContacts, 10);
  }

  // Build OutboundCallerConfig if any outbound fields are present
  const hasOutboundConfig =
    excelRow.outboundCallerIdName ||
    excelRow.outboundCallerIdNumber ||
    excelRow.outboundFlowName;

  if (hasOutboundConfig) {
    input.OutboundCallerConfig = {};

    if (excelRow.outboundCallerIdName) {
      input.OutboundCallerConfig.OutboundCallerIdName = excelRow.outboundCallerIdName;
    }

    if (excelRow.outboundCallerIdNumber && resolvedIds.phoneNumberToId) {
      // Normalize phone number to E.164 format (add + if missing)
      const normalizedPhone = normalizePhoneNumber(excelRow.outboundCallerIdNumber);
      input.OutboundCallerConfig.OutboundCallerIdNumberId =
        resolvedIds.phoneNumberToId[normalizedPhone];
    }

    if (excelRow.outboundFlowName && resolvedIds.contactFlowNameToId) {
      input.OutboundCallerConfig.OutboundFlowId =
        resolvedIds.contactFlowNameToId[excelRow.outboundFlowName];
    }
  }

  return input;
};

/**
 * Parse associated queues string from Excel
 * Uses pipe delimiter to support queue names containing commas
 * Supports both formats:
 * - Long format: "Queue1 (Priority: 1, Delay: 0) | Queue2 (Priority: 2, Delay: 5) | ..."
 * - Short format: "Queue1 (P:1, D:0) | Queue2 (P:2, D:5) | ..."
 * Returns array of queue names with priority and delay
 */
export const parseAssociatedQueues = (associatedQueuesString) => {
  if (!associatedQueuesString || associatedQueuesString === 'N/A') {
    return [];
  }

  const queues = [];
  // Split on pipe delimiter (with optional surrounding whitespace)
  const queueEntries = associatedQueuesString.split(/\s*\|\s*/).map(s => s.trim());

  queueEntries.forEach(entry => {
    // Try long format: "Queue Name (Priority: 1, Delay: 0)"
    let match = entry.match(/^(.+?)\s*\(Priority:\s*(\d+),\s*Delay:\s*(\d+)\)$/);

    if (!match) {
      // Try short format: "Queue Name (P:1, D:0)" or "Queue Name (P: 1, D: 0)"
      match = entry.match(/^(.+?)\s*\(P:\s*(\d+),\s*D:\s*(\d+)\)$/);
    }

    if (match) {
      queues.push({
        name: match[1].trim(),
        priority: parseInt(match[2], 10),
        delay: parseInt(match[3], 10)
      });
    } else if (entry) {
      // Fallback: just the queue name without config
      queues.push({
        name: entry.trim(),
        priority: 1,
        delay: 0
      });
    }
  });

  return queues;
};

/**
 * Parse media concurrency from Excel channels column
 * Format: "VOICE, CHAT, TASK"
 * Returns MediaConcurrencies array for AWS SDK
 */
export const parseMediaConcurrencies = (excelRow) => {
  const concurrencies = [];

  // Parse channels string (e.g., "VOICE, CHAT, TASK")
  const channels = excelRow.channels ? excelRow.channels.split(',').map(c => c.trim()) : [];

  channels.forEach(channel => {
    const concurrency = {
      Channel: channel,
      Concurrency: 1, // Default
      CrossChannelBehavior: {
        BehaviorType: 'ROUTE_CURRENT_CHANNEL_ONLY'
      }
    };

    // Parse specific channel concurrency from dedicated columns
    // Format: "2 - ROUTE_CURRENT_CHANNEL_ONLY" or just "2"
    let concurrencyValue = null;
    let behaviorType = null;

    switch (channel) {
      case 'CHAT':
        if (excelRow.chatConcurrency) {
          const parsed = parseConcurrencyString(excelRow.chatConcurrency);
          concurrencyValue = parsed.concurrency;
          behaviorType = parsed.behaviorType;
        }
        break;
      case 'TASK':
        if (excelRow.taskConcurrency) {
          const parsed = parseConcurrencyString(excelRow.taskConcurrency);
          concurrencyValue = parsed.concurrency;
          behaviorType = parsed.behaviorType;
        }
        break;
      case 'EMAIL':
        if (excelRow.emailConcurrency) {
          const parsed = parseConcurrencyString(excelRow.emailConcurrency);
          concurrencyValue = parsed.concurrency;
          behaviorType = parsed.behaviorType;
        }
        break;
      default:
        // VOICE or other channels - use default
        break;
    }

    if (concurrencyValue !== null) {
      concurrency.Concurrency = concurrencyValue;
    }

    if (behaviorType !== null) {
      concurrency.CrossChannelBehavior.BehaviorType = behaviorType;
    }

    concurrencies.push(concurrency);
  });

  return concurrencies;
};

/**
 * Parse concurrency string from Excel
 * Format: "2 - ROUTE_CURRENT_CHANNEL_ONLY" or just "2"
 */
const parseConcurrencyString = (concurrencyStr) => {
  if (!concurrencyStr) {
    return { concurrency: 1, behaviorType: 'ROUTE_CURRENT_CHANNEL_ONLY' };
  }

  const parts = concurrencyStr.split(' - ').map(s => s.trim());

  const concurrency = parseInt(parts[0], 10) || 1;
  const behaviorType = parts[1] || 'ROUTE_CURRENT_CHANNEL_ONLY';

  return { concurrency, behaviorType };
};

/**
 * Map Routing Profile Excel row to AWS SDK CreateRoutingProfileCommand input
 * Note: Requires resolved queue IDs
 */
export const mapRoutingProfileForCreate = (excelRow, resolvedIds = {}) => {
  const input = {
    Name: excelRow.name || '',
    Description: excelRow.description || '',
    MediaConcurrencies: parseMediaConcurrencies(excelRow),
    Tags: {}
  };

  // Resolve default outbound queue ID
  if (excelRow.defaultOutboundQueue && resolvedIds.queueNameToId) {
    input.DefaultOutboundQueueId = resolvedIds.queueNameToId[excelRow.defaultOutboundQueue];
  }

  // Parse and resolve associated queues
  const associatedQueues = parseAssociatedQueues(excelRow.associatedQueues);

  if (associatedQueues.length > 0 && resolvedIds.queueNameToId) {
    input.QueueConfigs = associatedQueues.map(queue => {
      const queueId = resolvedIds.queueNameToId[queue.name];

      if (!queueId) {
        console.warn(`Queue not found: ${queue.name}`);
        return null;
      }

      return {
        QueueReference: {
          QueueId: queueId,
          Channel: 'VOICE' // Default channel, can be extended
        },
        Priority: queue.priority,
        Delay: queue.delay
      };
    }).filter(Boolean); // Remove null entries
  }

  return input;
};

/**
 * Map Quick Connect Excel row to AWS SDK CreateQuickConnectCommand input
 * Note: Requires resolved IDs for queue, user, and contact flow references
 */
export const mapQuickConnectForCreate = (excelRow, resolvedIds = {}) => {
  const input = {
    Name: excelRow.name || '',
    Description: excelRow.description || '',
    QuickConnectConfig: {},
    Tags: {}
  };

  const type = excelRow.type ? excelRow.type.toUpperCase() : 'PHONE_NUMBER';

  switch (type) {
    case 'PHONE_NUMBER':
      input.QuickConnectConfig.QuickConnectType = 'PHONE_NUMBER';
      input.QuickConnectConfig.PhoneConfig = {
        PhoneNumber: excelRow.destination || excelRow.targetName || ''
      };
      break;

    case 'QUEUE':
      input.QuickConnectConfig.QuickConnectType = 'QUEUE';

      // Resolve queue ID
      const queueId = excelRow.targetName && resolvedIds.queueNameToId
        ? resolvedIds.queueNameToId[excelRow.targetName]
        : null;

      // Resolve contact flow ID
      const queueFlowId = excelRow.contactFlowName && resolvedIds.contactFlowNameToId
        ? resolvedIds.contactFlowNameToId[excelRow.contactFlowName]
        : null;

      input.QuickConnectConfig.QueueConfig = {
        QueueId: queueId || '',
        ContactFlowId: queueFlowId || ''
      };
      break;

    case 'USER':
      input.QuickConnectConfig.QuickConnectType = 'USER';

      // Resolve user ID
      const userId = excelRow.targetName && resolvedIds.userNameToId
        ? resolvedIds.userNameToId[excelRow.targetName]
        : null;

      // Resolve contact flow ID
      const userFlowId = excelRow.contactFlowName && resolvedIds.contactFlowNameToId
        ? resolvedIds.contactFlowNameToId[excelRow.contactFlowName]
        : null;

      input.QuickConnectConfig.UserConfig = {
        UserId: userId || '',
        ContactFlowId: userFlowId || ''
      };
      break;

    default:
      console.warn(`Unknown Quick Connect type: ${type}`);
      break;
  }

  return input;
};

/**
 * Map User Hierarchy Excel row to AWS SDK CreateUserHierarchyGroupCommand input
 * Note: Requires resolved parent group IDs
 */
export const mapUserHierarchyForCreate = (excelRow, resolvedIds = {}, level = 1) => {
  const levelKey = `level${level}`;
  const name = excelRow[levelKey];

  if (!name || name === 'N/A') {
    return null;
  }

  const input = {
    Name: name,
    Tags: {}
  };

  // If not level 1, resolve parent group ID
  if (level > 1) {
    const parentLevelKey = `level${level - 1}`;
    const parentName = excelRow[parentLevelKey];

    if (parentName && parentName !== 'N/A' && resolvedIds.hierarchyGroupNameToId) {
      input.ParentGroupId = resolvedIds.hierarchyGroupNameToId[parentName];
    }
  }

  return input;
};

/**
 * Build hierarchical structure from User Hierarchy Excel data
 * Returns array of hierarchy objects in order (parent before children)
 */
export const buildUserHierarchyStructure = (excelData) => {
  const hierarchyGroups = [];
  const processedGroups = new Set();

  // Process each row and extract unique groups at each level
  excelData.forEach(row => {
    for (let level = 1; level <= 5; level++) {
      const levelKey = `level${level}`;
      const groupName = row[levelKey];

      if (!groupName || groupName === 'N/A') {
        break; // Stop at first empty level
      }

      // Build unique key including parent path
      let uniqueKey = groupName;
      let parentName = null;

      if (level > 1) {
        const parentLevelKey = `level${level - 1}`;
        parentName = row[parentLevelKey];
        uniqueKey = `${parentName}|${groupName}`; // Parent|Child
      }

      // Add to hierarchy if not already processed
      if (!processedGroups.has(uniqueKey)) {
        processedGroups.add(uniqueKey);

        hierarchyGroups.push({
          name: groupName,
          level: level,
          parentName: parentName,
          uniqueKey: uniqueKey,
          row: { ...row } // Keep original row data for reference
        });
      }
    }
  });

  // Sort by level to ensure parents are created before children
  hierarchyGroups.sort((a, b) => a.level - b.level);

  return hierarchyGroups;
};
